import { addDays, addWeeks, differenceInMonths, format, parse } from 'date-fns'

export const timeUtil = {
  customFormatToDate: (dateString: string, customFormat: string): Date => {
    return parse(dateString, customFormat, new Date())
  },
  stringToDate: (dateString: string): Date => {
    return new Date(dateString)
  },
  now: (): Date => {
    return new Date()
  },
  stringToDateFormat: (dateString: string, { dateSep }: { dateSep?: string } = {}): string => {
    const date = timeUtil.stringToDate(dateString)
    return format(date, ['dd', 'MM', 'yyyy'].join(dateSep ?? '/'))
  },
  stringToDateTimeFormat: (
    dateString: string,
    { dateSep, timeSep, dateTimeSep }: { dateSep?: string; timeSep?: string; dateTimeSep?: string } = {},
  ): string => {
    const date = timeUtil.stringToDate(dateString)
    return format(date, [['HH', 'mm'].join(timeSep ?? ':'), ['dd', 'MM', 'yyyy'].join(dateSep ?? '/')].join(dateTimeSep ?? ' '))
  },
  stringToTimeFormat: (dateString: string, { timeSep }: { timeSep?: string } = {}): string => {
    const date = timeUtil.stringToDate(dateString)
    return format(date, ['HH', 'mm'].join(timeSep ?? ':'))
  },
  fromToDateTimeFormat: (
    fromString: string,
    toString: string,
    options: { dateSep?: string; timeSep?: string; dateTimeSep?: string; fromToSep?: string } = {},
  ): string => {
    const fromTime = timeUtil.stringToTimeFormat(fromString, options)
    const toTime = timeUtil.stringToTimeFormat(toString, options)
    const fromDate = timeUtil.stringToDateFormat(fromString, options)
    const toDate = timeUtil.stringToDateFormat(toString, options)
    const from = fromDate === toDate ? fromTime : [fromTime, fromDate].join(' ')
    const to = [toTime, toDate].join(' ')
    return [from, to].join(options.fromToSep ?? ' - ')
  },
  stringToUnixMS: (dateString: string): number => {
    return timeUtil.stringToDate(dateString).getTime()
  },
  localToUtcDateTime: (localDate: Date): Date => {
    const offset = localDate.getTimezoneOffset() * 60 * 1000
    return new Date(localDate.getTime() - offset)
  },
  dateTimeToDateString: (date: Date): string => {
    return format(date, 'yyyy-MM-dd')
  },
  dateToDayOfWeek: (date: Date): string => {
    return format(date, 'EEEE')
  },
  dateToMonth: (date: Date): string => {
    return format(date, 'MMMM')
  },
  dateToDayOfWeekId: (date: Date): number => {
    return date.getDay()
  },
  formatInDate: (date: Date): string => {
    return format(date, 'yyyy-MM-dd')
  },
  dateToDateFormatWithShortMonth: (date: Date | number): string => {
    return format(date, ['d', 'MMM', 'yyyy'].join(' ')).toLowerCase()
  },
  timestampToDateFormatWithNumericMonth: (date: Date | number): string => {
    return format(date, ['d', 'M', 'yy'].join('/')).toLowerCase()
  },
  addDays: (timestamp: number | Date, numberOfDays: number): Date => {
    return addDays(timestamp, numberOfDays)
  },
  addWeeks: (timestamp: number | Date, numberOfWeeks: number): Date => {
    return addWeeks(timestamp, numberOfWeeks)
  },
  timestampToMonth: (timestamp: number): string => {
    const date = new Date(timestamp)
    return timeUtil.dateToMonth(date)
  },
  dateToMonthYear: (date: Date): string => {
    return format(date, 'MMMM yyyy')
  },
  formatRelativeTime: (
    input: string | number,
    { addAgoSuffix = false, fallback = 'recently' }: { addAgoSuffix?: boolean; fallback?: string } = {},
  ): string => {
    // Handle undefined/null input
    if (!input) return fallback

    // Convert input to timestamp
    let timestamp: number
    if (typeof input === 'string') {
      const numericValue = Number(input)
      if (!isNaN(numericValue)) {
        // Numeric string (e.g., "1702567890" from bigint column)
        // Convert seconds to milliseconds if needed (10 digits = seconds, 13 digits = milliseconds)
        timestamp = numericValue < 10000000000 ? numericValue * 1000 : numericValue
      } else {
        // ISO date string
        timestamp = new Date(input).getTime()
      }
    } else {
      // Number - convert seconds to ms if needed
      timestamp = input < 10000000000 ? input * 1000 : input
    }

    // Validate timestamp is not NaN
    if (isNaN(timestamp)) return fallback

    const now = Date.now()
    let diffInSeconds = Math.floor((now - timestamp) / 1000)
    if (diffInSeconds < 0) diffInSeconds = 0

    // Less than a minute
    if (diffInSeconds < 60) {
      return addAgoSuffix ? 'just now' : 'now'
    }

    // Minutes
    const diffInMinutes = Math.floor(diffInSeconds / 60)
    if (diffInMinutes < 60) {
      return addAgoSuffix ? `${diffInMinutes}m ago` : `${diffInMinutes}m`
    }

    // Hours
    const diffInHours = Math.floor(diffInMinutes / 60)
    if (diffInHours < 24) {
      return addAgoSuffix ? `${diffInHours}h ago` : `${diffInHours}h`
    }

    // Days
    const diffInDays = Math.floor(diffInHours / 24)
    if (diffInDays < 7) {
      return addAgoSuffix ? `${diffInDays}d ago` : `${diffInDays}d`
    }

    // Weeks
    const diffInWeeks = Math.floor(diffInDays / 7)
    if (diffInWeeks < 4) {
      return addAgoSuffix ? `${diffInWeeks}w ago` : `${diffInWeeks}w`
    }

    // Months
    const diffInMonths = differenceInMonths(now, timestamp)
    return addAgoSuffix ? `${diffInMonths}mo ago` : `${diffInMonths}mo`
  },
}
